import { useMemo } from "react";
import { HatimIcon, MuzakereDoneIcon, MuzakereIcon } from "./mdiIcons";
import FlagIcon from "@mui/icons-material/Flag";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import { buildMonthDays, DAY_STATUS } from "../../lib/calendarDays";

const TR_DAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

const CELL_STYLE = {
  [DAY_STATUS.PERFECT]: "bg-terakkiPrimaryDark text-white border-terakkiPrimaryDark shadow-sm dark:bg-terakkiBrand dark:border-terakkiBrand",
  [DAY_STATUS.PARTIAL]: "bg-terakkiBrandCloud text-terakkiInk border-terakkiBrandMist dark:bg-white/15 dark:text-white dark:border-white/20",
  [DAY_STATUS.ZERO]: "bg-terakkiSurface text-terakkiNeutral border-terakkiError/30 dark:bg-white/5 dark:text-white/70 dark:border-[#F2A59C]/40",
  [DAY_STATUS.MISSED]: "bg-terakkiSurfaceAlt text-terakkiNeutral border-dashed border-terakkiBorder dark:bg-white/5 dark:text-white/60 dark:border-white/20",
  [DAY_STATUS.TODAY_EMPTY]: "bg-terakkiSurface text-terakkiPrimaryDark border-terakkiPrimaryDark dark:bg-white/5 dark:text-terakkiBrandSoft dark:border-terakkiBrandSoft",
  [DAY_STATUS.FUTURE]: "bg-terakkiSurface text-terakkiBorder border-terakkiBorder/60 dark:bg-transparent dark:text-white/30 dark:border-white/10",
};

function DayCell({ day, onSelect, delay = 0 }) {
  const { status, isSelected, isToday, isFuture, percent, isPerfect } = day;
  const showPercent = day.hasLog && !isPerfect && percent > 0;

  return (
    // Merdiven gibi belirme: gecikme hücrenin satır + sütun sırasından (çaprazlama dalga).
    <div className="relative motion-safe:animate-pop-in" style={{ animationDelay: `${delay}ms` }}>
      {/* Zincir: ardışık mükemmel günleri ızgara boşluğu üzerinden birleştirir */}
      {day.chainLeft && (
        <span
          aria-hidden
          data-chain="left"
          className="absolute top-1/2 -left-2 w-2 h-1.5 -translate-y-1/2 bg-terakkiPrimaryDark dark:bg-terakkiBrand"
        />
      )}
      {day.chainRight && (
        <span
          aria-hidden
          data-chain="right"
          className="absolute top-1/2 -right-2 w-2 h-1.5 -translate-y-1/2 bg-terakkiPrimaryDark dark:bg-terakkiBrand"
        />
      )}

      <button
        type="button"
        data-day={day.date}
        data-status={status}
        aria-pressed={isSelected}
        disabled={isFuture}
        aria-label={[
          day.date,
          isPerfect ? "tam" : day.hasLog ? `%${percent}` : "",
          day.hasHatim ? "hatim" : "",
          day.muzakere ? "müzakere" : "",
          day.kaza ? "kaza" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={() => onSelect && onSelect(day.date)}
        className={[
          "relative w-full aspect-[7/6] rounded-xl border p-1.5 flex flex-col justify-between",
          "transition-transform duration-100 hover:-translate-y-0.5 focus:outline-none",
          "focus-visible:ring-2 focus-visible:ring-terakkiPrimary focus-visible:ring-offset-1",
          CELL_STYLE[status],
          isSelected ? "ring-2 ring-terakkiPrimaryDark ring-offset-1 dark:ring-white dark:ring-offset-transparent" : "",
          isFuture ? "cursor-not-allowed" : "",
        ].join(" ")}
      >
        {/* Üst satır: ilerleme yüzdesi / yıldız */}
        <span className="flex items-start justify-between text-sm font-SemiBold leading-none min-h-[12px]">
          {showPercent ? <span>%{percent}</span> : <span />}
          {isPerfect && (
            <span data-star className="text-[13px] leading-none" title={`${day.streak} günlük zincir`}>
              ★
            </span>
          )}
        </span>

        <span className="text-md font-Bold leading-none text-center">{day.day}</span>

        {/* Sağ altta rozetler: hedef girilmişse bayrak, hatim varsa kitap,
            müzakere varsa sohbet (katılındıysa dolu, planlıysa içi boş; mobille aynı) */}
        <span className="flex items-center justify-end gap-1 min-h-[18px] leading-none">
          {day.goalCount > 0 && (
            <FlagIcon data-badge="goal" sx={{ fontSize: 18, opacity: 0.8 }} aria-hidden />
          )}
          {day.muzakere === "done" && (
            <MuzakereDoneIcon data-badge="muzakere-done" sx={{ fontSize: 17, opacity: 0.95 }} aria-hidden />
          )}
          {day.muzakere === "planned" && (
            <MuzakereIcon data-badge="muzakere-planned" sx={{ fontSize: 17, opacity: 0.7 }} aria-hidden />
          )}
          {day.kaza && (
            <BedtimeIcon data-badge="kaza" sx={{ fontSize: 16, opacity: 0.9 }} aria-hidden />
          )}
          {day.hasHatim && (
            <HatimIcon data-badge="hatim" sx={{ fontSize: 18, opacity: 0.95 }} aria-hidden />
          )}
        </span>

        {/* Alt: ilerleme çubuğu, mükemmel günde tam dolu; koyu temada beyaz (mobille aynı) */}
        <span className="block h-1 rounded-full bg-black/5 dark:bg-white/15 overflow-hidden">
          <span
            data-bar
            className={`block h-full rounded-full ${isPerfect ? "bg-white" : "bg-terakkiPrimaryDark dark:bg-white"}`}
            style={{ width: `${day.hasLog ? percent : 0}%` }}
          />
        </span>

        {isToday && (
          <span
            aria-hidden
            className="absolute -bottom-[3px] left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-terakkiPrimaryDark dark:bg-white"
          />
        )}
      </button>
    </div>
  );
}

export default function MonthCalendar({
  monthDate,
  dailyLogs = [],
  selectedDate,
  onDayClick,
  today,
  hatimDates,
  muzakereMarks,
  kazaMarks,
}) {
  const { offset, days } = useMemo(() => {
    const built = buildMonthDays({ monthDate, dailyLogs, selectedDate, today: today || new Date(), hatimDates });
    // Müzakere işareti gün → "done" | "planned" haritasından (lib/dayMarkers).
    // Kaza işareti gün → true haritasından (o gün kaza kılındıysa).
    if (!muzakereMarks && !kazaMarks) return built;
    return {
      ...built,
      days: built.days.map((d) => ({
        ...d,
        muzakere: muzakereMarks?.[d.date] || null,
        kaza: Boolean(kazaMarks?.[d.date]),
      })),
    };
  }, [monthDate, dailyLogs, selectedDate, today, hatimDates, muzakereMarks, kazaMarks]);

  return (
    <div className="w-full">
      <div className="grid grid-cols-7 gap-2 mb-2">
        {TR_DAYS.map((d) => (
          <div key={d} className="text-sm text-terakkiNeutral text-center font-SemiBold uppercase tracking-wide dark:text-white/60">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {Array.from({ length: offset }, (_, i) => (
          <div key={`pad-${i}`} aria-hidden />
        ))}
        {days.map((day, index) => (
          <DayCell
            key={day.date}
            day={day}
            onSelect={onDayClick}
            delay={(Math.floor((offset + index) / 7) + ((offset + index) % 7)) * 35}
          />
        ))}
      </div>
    </div>
  );
}
